/**
 * Per-line-item pacing visuals.
 *
 * The tracker sheet plans impressions PER LINE ITEM under each IO. The backend
 * matches each sheet plan to its GAM line (invoicePlannedMatching) so every
 * line paces against ITS OWN plan, falling back to the GAM goal when no match
 * exists; the invoice-level rollup counts only the lines flighted in the
 * billing month and prefers the TRUE per-month delivered figure (date-ranged
 * report) once "Refresh delivered" / the nightly refresh has run. Two surfaces:
 *
 *   - PacingGlance: a one-line microline for the collapsed table cell —
 *     segmented health strip + "N LI · n off-pace". Only renders for
 *     multi-line orders so the common single-line case stays as simple as
 *     before.
 *   - DeliveryPacingPanel: the expanded row's unified "Delivery & pacing"
 *     section — month rollup bar + one labeled bar per line item, with
 *     out-of-month lines (the rest of the order) collapsed behind a toggle.
 *
 * No chart library — matches the house style (hand-rolled div widgets).
 */

import React, { useState } from 'react';
import { PacingBar, PacingTrack, fillColor, fmtInt, fmtPct, Spinner } from '../ui';
import { fmtDateRange, fmtAsOf } from './dateFormat';
import { gamSyncState } from './gamSync';
import { DeliverySnapshot, Invoice, LineItemDelivery } from './types';

// ---------- Line health ----------

export type LineHealth = 'over' | 'behind' | 'ahead' | 'ontrack' | 'done' | 'ready' | 'nogoal';

/**
 * Each line is measured against its SHEET plan when the backend matched one
 * (plannedImpressions), else its GAM goal. A plan denominator pairs with the
 * month-true numerator when available (a line spanning several months would
 * otherwise compare lifetime delivered against one month's plan); the GAM-goal
 * denominator keeps the lifetime numerator (both are whole-flight figures).
 */
export const lineDenominator = (li: LineItemDelivery): number => li.plannedImpressions ?? li.goal;
export const lineNumerator = (li: LineItemDelivery): number =>
  li.plannedImpressions !== undefined ? li.deliveredMonth ?? li.delivered : li.delivered;
export const pctOfPlan = (li: LineItemDelivery): number | undefined => {
  const denom = lineDenominator(li);
  return denom > 0 ? (lineNumerator(li) / denom) * 100 : undefined;
};

/**
 * One line item's pacing health, mirroring the backend thresholds
 * (invoicePacingService: <98 behind / >105 ahead, over-delivery guard at
 * 90% / 100% of its plan). COMPLETED lines read as done (they can't grow — the
 * billing overage check lives at the invoice level vs contracted).
 */
export function lineHealth(li: LineItemDelivery): LineHealth {
  const pct = pctOfPlan(li);
  if (pct === undefined) return 'nogoal';
  if (/complete/i.test(li.status || '')) return 'done';
  if (pct >= 100) return 'over';
  if (li.delivered === 0 && /ready|draft|inactive|paused|pending/i.test(li.status || '')) return 'ready';
  if (pct >= 90) return 'ahead'; // approaching its plan — same 0.9 guard as the invoice rollup
  if (li.progressPercent < 98) return 'behind';
  if (li.progressPercent > 105) return 'ahead';
  return 'ontrack';
}

export const healthColor: Record<LineHealth, string> = {
  over: 'var(--danger)',
  behind: 'var(--danger)',
  ahead: 'var(--warning)',
  ontrack: 'var(--success)',
  done: 'var(--success)',
  ready: 'var(--fg-4)',
  nogoal: 'var(--fg-4)',
};

export const isOffPace = (h: LineHealth): boolean => h === 'over' || h === 'behind';

const SEVERITY: Record<LineHealth, number> = { over: 0, behind: 1, ahead: 2, ontrack: 3, done: 4, ready: 5, nogoal: 6 };
const bySeverity = (a: LineItemDelivery, b: LineItemDelivery) =>
  SEVERITY[lineHealth(a)] - SEVERITY[lineHealth(b)] ||
  (a.lineItemName || a.lineItemId).localeCompare(b.lineItemName || b.lineItemId);

/** In-month vs rest-of-order. Pre-migration snapshots (no inMonth) = all in-month. */
export function splitLines(d: DeliverySnapshot): { inMonth: LineItemDelivery[]; other: LineItemDelivery[] } {
  const lines = d.lineItems ?? [];
  return {
    inMonth: lines.filter((li) => li.inMonth !== false),
    other: lines.filter((li) => li.inMonth === false),
  };
}

/** Expected-by-now % for a line — direct from GAM, or derived from on-target %. */
const lineExpected = (li: LineItemDelivery): number | undefined =>
  li.expectedPercent ?? (li.progressPercent > 0 ? (li.percentDelivered / li.progressPercent) * 100 : undefined);

// ---------- Plan groups (packaged buys) ----------

/**
 * One sheet plan row trafficked as SEVERAL GAM lines (joined by placement code
 * — see backend invoicePlannedMatching). The plan paces the group's summed
 * month delivery, so the panel shows ONE pacing row per plan with its member
 * lines tucked behind a toggle.
 */
export interface PlanGroupView {
  key: string;
  label?: string;
  plannedImpressions: number;
  lines: LineItemDelivery[];
  /** This month's delivery summed across the group's lines. */
  delivered: number;
  /** delivered / plan, % — the group's pacing. */
  pct?: number;
}

/** Cluster in-month lines by their sheet plan group; the rest stay per-line. */
export function groupPlans(inMonth: LineItemDelivery[]): { groups: PlanGroupView[]; ungrouped: LineItemDelivery[] } {
  const byKey = new Map<string, PlanGroupView>();
  const ungrouped: LineItemDelivery[] = [];
  for (const li of inMonth) {
    if (!li.planGroup) {
      ungrouped.push(li);
      continue;
    }
    let g = byKey.get(li.planGroup.key);
    if (!g) {
      g = {
        key: li.planGroup.key,
        label: li.planGroup.label,
        plannedImpressions: li.planGroup.plannedImpressions,
        lines: [],
        delivered: 0,
      };
      byKey.set(li.planGroup.key, g);
    }
    g.lines.push(li);
    g.delivered += li.deliveredMonth ?? li.delivered;
  }
  const groups = Array.from(byKey.values()).map((g) => ({
    ...g,
    pct: g.plannedImpressions > 0 ? (g.delivered / g.plannedImpressions) * 100 : undefined,
  }));
  return { groups, ungrouped };
}

/** A group's health vs ITS plan, using the invoice's window expectation. */
export function groupHealth(g: PlanGroupView, expectedPercent?: number): LineHealth {
  if (g.pct === undefined) return 'nogoal';
  if (g.lines.every((li) => /complete/i.test(li.status || ''))) return 'done';
  if (g.pct >= 100) return 'over';
  if (g.pct >= 90) return 'ahead'; // approaching its plan — same 0.9 guard as the rollup
  if (expectedPercent && expectedPercent > 0) {
    const progress = (g.pct / expectedPercent) * 100;
    if (progress < 98) return 'behind';
    if (progress > 105) return 'ahead';
  }
  return 'ontrack';
}

// ---------- PacingGlance (collapsed cell) ----------

const GLANCE_MAX_SEGS = 10;

/**
 * Collapsed-cell microline under the aggregate bar: a segmented health strip
 * (one segment per in-month line, worst first) + off-pace count. Hidden for
 * single-line orders and unsynced rows — the aggregate bar already says it all.
 */
export const PacingGlance: React.FC<{ delivery: DeliverySnapshot }> = ({ delivery }) => {
  if (delivery.percentDelivered === undefined) return null;
  const { inMonth } = splitLines(delivery);
  // One glance item per SHEET PLAN where lines are grouped (a packaged buy
  // paces as one unit), per line otherwise.
  const { groups, ungrouped } = groupPlans(inMonth);
  const items = [
    ...groups.map((g) => ({ id: `plan:${g.key}`, name: g.key, health: groupHealth(g, delivery.expectedPercent) })),
    ...ungrouped.map((li) => ({ id: li.lineItemId, name: li.lineItemName || li.lineItemId, health: lineHealth(li) })),
  ];
  if (items.length < 2) return null;

  const sorted = [...items].sort((a, b) => SEVERITY[a.health] - SEVERITY[b.health] || a.name.localeCompare(b.name));
  const off = sorted.filter((it) => isOffPace(it.health));
  const unit = groups.length > 0 ? (ungrouped.length ? 'plans/LI' : 'plans') : 'LI';
  const tip =
    `${items.length} ${groups.length ? 'sheet plans' : 'line items'} this month` +
    (off.length ? ` — off pace: ${off.map((it) => it.name).join(', ')}` : '');

  return (
    <div style={glance.wrap} title={tip}>
      <span style={glance.strip} aria-hidden="true">
        {sorted.slice(0, GLANCE_MAX_SEGS).map((it) => (
          <span key={it.id} style={{ ...glance.seg, background: healthColor[it.health] }} />
        ))}
      </span>
      <span style={glance.count}>
        {items.length > GLANCE_MAX_SEGS ? `${items.length} ${unit} (+${items.length - GLANCE_MAX_SEGS})` : `${items.length} ${unit}`}
      </span>
      {off.length > 0 && <span style={glance.alert}>{off.length} off-pace</span>}
    </div>
  );
};

const glance: Record<string, React.CSSProperties> = {
  wrap: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, minWidth: 96 },
  strip: { display: 'flex', gap: 1, width: 44, height: 3, borderRadius: 2, overflow: 'hidden', flexShrink: 0 },
  seg: { flex: 1 },
  count: { fontSize: 10, color: 'var(--fg-4)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  alert: { fontSize: 10, fontWeight: 700, color: 'var(--danger)', whiteSpace: 'nowrap' },
};

// ---------- DeliveryPacingPanel (expanded detail) ----------

const MAX_VISIBLE_LINES = 8;

interface PanelProps {
  invoice: Invoice;
  /** 3P verification on — GAM lines are frozen; aggregate follows the 3P number. */
  threeP: boolean;
  noDataLabel?: string;
}

const DeliveryPacingPanel: React.FC<PanelProps> = ({ invoice, threeP, noDataLabel }) => {
  const [showOther, setShowOther] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // Plan groups whose member lines are expanded (collapsed by default — the
  // group row carries the pacing; members are the trafficking detail).
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());

  const delivery = invoice.delivery;
  const contracted = invoice.contractedImpressions;
  const { inMonth, other } = splitLines(delivery);
  const lines = delivery.lineItems ?? [];
  const exceeded = delivery.overDeliveryFlag === 'exceeded';
  const aggColor = fillColor(delivery);

  // GAM data pending (linked 1P row whose month-true report hasn't run for the
  // current link): render a sync placeholder instead of figures — the only
  // numbers available are lifetime sums, which overstate multi-month campaigns.
  const syncState = gamSyncState(invoice);
  if (!threeP && syncState !== 'ready') {
    return (
      <div style={s.wrap}>
        <div style={s.syncWrap} role="status">
          {syncState === 'syncing' && <Spinner size={12} />}
          <div>
            <div style={s.syncTitle}>
              {syncState === 'syncing'
                ? 'Syncing GAM — fetching this month’s delivered impressions…'
                : syncState === 'failed'
                  ? 'GAM sync failed'
                  : 'Awaiting GAM sync'}
            </div>
            <div style={s.caption}>
              {syncState === 'failed'
                ? `${invoice.delivery.monthDeliveredError?.message ?? 'The date-ranged GAM report errored.'} — use “Refresh delivered” to retry.`
                : 'Pacing figures appear when the date-ranged GAM report for this billing month completes — partial or lifetime numbers are never shown.'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No per-line data → the classic aggregate panel (covers unlinked rows,
  // linked-but-unpaced rows, and 3P rows whose GAM history was never synced).
  if (lines.length === 0) {
    return (
      <div style={s.wrap}>
        {threeP && <ThreePNote />}
        <PacingBar delivery={delivery} variant="panel" contracted={contracted} noDataLabel={noDataLabel} />
        <div style={s.caption}>
          {invoice.gam.gamLineItemIds.length
            ? `Linked line items (not paced yet): ${invoice.gam.gamLineItemIds.join(', ')}`
            : 'Not linked to GAM.'}
        </div>
      </div>
    );
  }

  // Packaged buys pace as plan GROUPS (one row per sheet plan, members behind a
  // toggle); everything else stays per-line.
  const { groups, ungrouped } = groupPlans(inMonth);
  const groupsSorted = [...groups].sort(
    (a, b) =>
      SEVERITY[groupHealth(a, delivery.expectedPercent)] - SEVERITY[groupHealth(b, delivery.expectedPercent)] ||
      a.key.localeCompare(b.key),
  );
  const ungroupedSorted = [...ungrouped].sort(bySeverity);
  const visible = showAll ? ungroupedSorted : ungroupedSorted.slice(0, MAX_VISIBLE_LINES);

  // The month rollup's numerator: the TRUE per-month figure (date-ranged GAM
  // report). The sync gate above guarantees it exists for 1P rows — there is
  // deliberately NO lifetime fallback here (a line item spanning several months
  // would overstate every month after its first).
  const monthTrue = delivery.impressionsDeliveredMonth;
  const rollupDelivered = monthTrue;
  const monthTrueNote =
    monthTrue !== undefined
      ? `True per-month delivered (date-ranged GAM report)${
          delivery.monthDeliveredAsOf ? `, ${fmtAsOf(delivery.monthDeliveredAsOf)}` : ''
        }`
      : undefined;

  // Sheet-planned vs GAM reconciliation: once every in-month line carries a
  // matched sheet plan (its own, or via a plan group), compare the plans' sum
  // against the stored monthly total; otherwise fall back to the GAM goal sum
  // (when comparable at all — DAILY/house lines have no fixed goal).
  const matchedCount = ungrouped.filter((li) => li.plannedImpressions !== undefined).length;
  const sheetRowsMatched = matchedCount + groups.length;
  const allMatched = inMonth.length > 0 && ungrouped.every((li) => li.plannedImpressions !== undefined);
  const plannedSum =
    ungrouped.reduce((sum, li) => sum + (li.plannedImpressions ?? 0), 0) +
    groups.reduce((sum, g) => sum + g.plannedImpressions, 0);
  const goalSum = inMonth.reduce((sum, li) => sum + (li.goal || 0), 0);
  const allHaveGoals = inMonth.length > 0 && inMonth.every((li) => li.goal > 0);
  const compareSum = allMatched ? plannedSum : goalSum;
  const mismatch =
    (allMatched || allHaveGoals) &&
    contracted !== undefined &&
    contracted > 0 &&
    Math.abs(compareSum - contracted) / contracted > 0.01;

  const wholeOrderScope = delivery.pacingScope === 'all-line-items';
  // What the list actually shows: one row per plan group + one per loose line.
  const displayUnits = groupsSorted.length + ungroupedSorted.length;

  return (
    <div style={s.wrap}>
      {threeP && <ThreePNote />}

      {threeP ? (
        // 3P: the agency number drives the aggregate readouts; GAM lines below are frozen.
        <PacingBar delivery={delivery} variant="panel" contracted={contracted} noDataLabel={noDataLabel} />
      ) : (
        exceeded && (
          <div style={s.overBanner}>
            ⚠ OVER-DELIVERED — delivered impressions have reached or exceeded the contracted goal.
          </div>
        )
      )}

      {/* Month rollup — only when several rows roll up (a single row IS the total). */}
      {!threeP && displayUnits > 1 && delivery.percentDelivered !== undefined && (
        <div className="inv-li-row inv-li-rollup">
          <span style={s.rollupLabel}>Month total{monthTrue !== undefined ? ' ✓' : ''}</span>
          <PacingTrack percent={delivery.percentDelivered} expected={delivery.expectedPercent} color={aggColor} />
          <span style={s.nums} title={monthTrueNote}>
            {fmtInt(rollupDelivered)} / {fmtInt(contracted)}
          </span>
          <span style={{ ...s.pct, color: aggColor, fontWeight: 700 }}>{fmtPct(delivery.percentDelivered)}</span>
          <span style={s.pct}>{fmtPct(delivery.progressPercent)}</span>
          <span />
          <span />
        </div>
      )}

      <div className={`inv-li-list${threeP ? ' inv-li-muted' : ''}`}>
        <div className="inv-li-row inv-li-head" aria-hidden="true">
          <span style={s.headLabel}>
            {groupsSorted.length > 0
              ? `Sheet plans (${groupsSorted.length}) · ${inMonth.length} line items`
              : inMonth.length > 1
                ? `Line items (${inMonth.length} this month)`
                : 'Line item'}
          </span>
          <span style={s.headLabel}>Pacing</span>
          <span style={{ ...s.headLabel, textAlign: 'right' }}>Delivered / Plan</span>
          <span style={{ ...s.headLabel, textAlign: 'right' }}>% plan</span>
          <span style={{ ...s.headLabel, textAlign: 'right' }}>On-target</span>
          <span style={s.headLabel}>Status</span>
          <span />
        </div>
        {groupsSorted.map((g) => (
          <React.Fragment key={g.key}>
            <GroupRow
              g={g}
              expectedPercent={delivery.expectedPercent}
              expanded={openGroups.has(g.key)}
              onToggle={() =>
                setOpenGroups((prev) => {
                  const next = new Set(prev);
                  if (next.has(g.key)) next.delete(g.key);
                  else next.add(g.key);
                  return next;
                })
              }
            />
            {openGroups.has(g.key) && (
              <div className="inv-li-muted">
                {[...g.lines].sort(bySeverity).map((li) => (
                  <LineRow key={li.lineItemId} li={li} />
                ))}
              </div>
            )}
          </React.Fragment>
        ))}
        {visible.map((li) => (
          <LineRow key={li.lineItemId} li={li} />
        ))}
      </div>
      {ungroupedSorted.length > MAX_VISIBLE_LINES && !showAll && (
        <button
          type="button"
          className="inv-btn inv-li-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setShowAll(true);
          }}
        >
          Show all {ungroupedSorted.length} line items
        </button>
      )}

      {other.length > 0 && (
        <>
          <button
            type="button"
            className="inv-btn inv-li-toggle"
            aria-expanded={showOther}
            onClick={(e) => {
              e.stopPropagation();
              setShowOther((v) => !v);
            }}
            title="Line items on this order whose flight falls outside this billing month — they don't count toward this month's pacing."
          >
            {showOther ? '▾' : '▸'} Other months ({other.length})
          </button>
          {showOther && (
            <div className="inv-li-list inv-li-muted">
              {[...other].sort(bySeverity).map((li) => (
                <LineRow key={li.lineItemId} li={li} showFlight />
              ))}
            </div>
          )}
        </>
      )}

      {mismatch && (
        <div
          style={s.mismatchNote}
          title={
            allMatched
              ? `The stored monthly Planned is ${fmtInt(contracted)} but the lines' matched sheet plans sum to ` +
                `${fmtInt(plannedSum)} (${sheetRowsMatched} of ${invoice.plannedLineItems?.length ?? sheetRowsMatched} sheet ` +
                `lines matched). A manual Planned edit or an unmatched sheet row usually explains the gap.`
              : `The sheet's Planned (month) is ${fmtInt(contracted)} but this month's GAM line goals sum to ` +
                `${fmtInt(goalSum)}. Check the tracker sheet or the GAM goals.`
          }
        >
          {allMatched
            ? `⚠ Planned ≠ line plans — ${fmtInt(contracted)} planned vs ${fmtInt(plannedSum)} across lines`
            : `⚠ Planned ≠ GAM goals — ${fmtInt(contracted)} planned vs ${fmtInt(goalSum)} in GAM`}
        </div>
      )}
      {wholeOrderScope && (
        <div style={s.caption}>
          Pacing covers the whole order — no line item's flight matched this billing month (check the line item dates
          in GAM).
        </div>
      )}
      {threeP && <div style={s.caption}>Last GAM (1P) line items — frozen while 3P verification is on.</div>}

      {!threeP && (
        <div style={s.caption}>
          {displayUnits <= 1 &&
            delivery.percentDelivered !== undefined &&
            `Month total ${fmtInt(rollupDelivered)} / ${fmtInt(contracted)} planned (${fmtPct(delivery.percentDelivered)}) · `}
          Expected {fmtPct(delivery.expectedPercent)} by now · {delivery.daysRemaining ?? '—'} days left
          {monthTrue !== undefined && (
            <span title={monthTrueNote}>
              {' '}· ✓ month-true{delivery.monthDeliveredAsOf ? ` ${fmtAsOf(delivery.monthDeliveredAsOf)}` : ''}
            </span>
          )}
          {delivery.lastPacingSyncAt && ` · synced ${new Date(delivery.lastPacingSyncAt).toLocaleString()}`}
        </div>
      )}
    </div>
  );
};

/**
 * One sheet plan (a packaged buy): pacing of the group's summed month delivery
 * against the sheet's planned impressions. The member GAM lines expand below.
 */
const GroupRow: React.FC<{
  g: PlanGroupView;
  expectedPercent?: number;
  expanded: boolean;
  onToggle: () => void;
}> = ({ g, expectedPercent, expanded, onToggle }) => {
  const health = groupHealth(g, expectedPercent);
  const color = healthColor[health];
  const progress = g.pct !== undefined && expectedPercent ? (g.pct / expectedPercent) * 100 : undefined;
  return (
    <div className="inv-li-row">
      <button
        type="button"
        className="inv-btn inv-li-grouptoggle"
        aria-expanded={expanded}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        title={
          `${g.label ?? g.key} — one sheet plan trafficked as ${g.lines.length} GAM line item(s); ` +
          `paced on the group's summed delivery this month.`
        }
      >
        {expanded ? '▾' : '▸'} {g.key}
      </button>
      <PacingTrack percent={g.pct ?? 0} expected={expectedPercent} color={color} height={6} />
      <span style={s.nums} title={`Delivered this month across ${g.lines.length} line item(s) / sheet plan`}>
        {fmtInt(g.delivered)} / {fmtInt(g.plannedImpressions)}
      </span>
      <span style={{ ...s.pct, color }}>{g.pct !== undefined ? `${g.pct.toFixed(0)}%` : '—'}</span>
      <span style={s.pct}>{progress !== undefined ? `${progress.toFixed(0)}%` : '—'}</span>
      <span style={s.liStatus}>{g.lines.length} lines</span>
      <span />
    </div>
  );
};

/** One line item: name, health-colored bar vs its own plan, numbers, status, GAM link. */
const LineRow: React.FC<{ li: LineItemDelivery; showFlight?: boolean }> = ({ li, showFlight }) => {
  const health = lineHealth(li);
  const color = healthColor[health];
  const denom = lineDenominator(li);
  const hasDenom = denom > 0;
  const pct = pctOfPlan(li);
  // Flag when the sheet's plan and the GAM goal disagree (>1%) — someone should
  // reconcile the tracker or the GAM booking.
  const planVsGoal =
    li.plannedImpressions !== undefined && li.goal > 0 && Math.abs(li.plannedImpressions - li.goal) / li.goal > 0.01;
  const numsTitle =
    [
      li.plannedImpressions !== undefined ? `Sheet planned ${fmtInt(li.plannedImpressions)}` : undefined,
      li.goal > 0 ? `GAM goal ${fmtInt(li.goal)}` : undefined,
      li.deliveredMonth !== undefined && li.deliveredMonth !== li.delivered
        ? `This month ${fmtInt(li.deliveredMonth)} · lifetime ${fmtInt(li.delivered)}`
        : undefined,
    ]
      .filter(Boolean)
      .join(' · ') || undefined;
  return (
    <div className="inv-li-row">
      <span className="inv-li-name" title={li.lineItemName || li.lineItemId}>
        {li.lineItemName || li.lineItemId}
        {showFlight && <span style={s.flightSub}> {fmtDateRange(li.startDate, li.endDate)}</span>}
      </span>
      {hasDenom ? (
        <PacingTrack percent={pct ?? 0} expected={lineExpected(li)} color={color} height={6} />
      ) : (
        <span style={s.noGoalTrack} title="No fixed impression goal (daily / house / sponsorship goal type)" />
      )}
      <span style={s.nums} title={numsTitle}>
        {fmtInt(lineNumerator(li))} / {hasDenom ? fmtInt(denom) : '—'}
        {planVsGoal && <span style={s.planMark}>*</span>}
      </span>
      <span style={{ ...s.pct, color: hasDenom ? color : 'var(--fg-4)' }}>
        {hasDenom && pct !== undefined ? `${pct.toFixed(0)}%` : '—'}
      </span>
      <span style={s.pct}>{hasDenom && li.progressPercent > 0 ? `${li.progressPercent.toFixed(0)}%` : '—'}</span>
      <span style={s.liStatus}>{li.status || '—'}</span>
      <span>
        {li.gamUrl && (
          <a
            href={li.gamUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={s.link}
            onClick={(e) => e.stopPropagation()}
          >
            GAM ↗
          </a>
        )}
      </span>
    </div>
  );
};

const ThreePNote: React.FC = () => (
  <div style={s.threePNote}>
    3P verification is on — GAM impressions are not fetched for this IO. Pacing follows the agency-confirmed 3P
    number; the last GAM (1P) figures are kept frozen for reference.
  </div>
);

const s: Record<string, React.CSSProperties> = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 10 },
  overBanner: {
    padding: '8px 12px',
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    borderRadius: 'var(--radius-md)',
    fontSize: 12.5,
    fontWeight: 600,
    border: '1px solid var(--danger)',
  },
  threePNote: {
    padding: '8px 12px',
    background: 'var(--accent-soft)',
    borderRadius: 'var(--radius-md)',
    fontSize: 12,
    color: 'var(--fg-2)',
    lineHeight: 1.45,
  },
  rollupLabel: { fontSize: 11.5, fontWeight: 700, color: 'var(--fg-2)' },
  headLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: 'var(--fg-4)',
  },
  nums: { fontSize: 11.5, color: 'var(--fg-2)', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  pct: { fontSize: 11.5, fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  liStatus: { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--fg-3)' },
  flightSub: { fontSize: 10, color: 'var(--fg-4)', marginLeft: 6 },
  noGoalTrack: {
    display: 'block',
    height: 6,
    borderRadius: 'var(--radius-pill)',
    background:
      'repeating-linear-gradient(90deg, var(--bg-surface-3) 0 6px, transparent 6px 10px)',
  },
  mismatchNote: { fontSize: 11.5, fontWeight: 600, color: 'var(--warning)' },
  planMark: { color: 'var(--warning)', fontWeight: 800, marginLeft: 1 },
  caption: { fontSize: 11, color: 'var(--fg-4)' },
  link: { fontSize: 11.5, color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 },
  syncWrap: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0' },
  syncTitle: { fontSize: 12.5, fontWeight: 600, color: 'var(--fg-2)', marginBottom: 2 },
};

export default DeliveryPacingPanel;
